import { Link } from 'react-router-dom';
import { useSeo } from '../lib/seo';

const LAST_UPDATED = '2026-05-10';

// PRIVACY POLICY — UK GDPR + DPA 2018 framing for B2B-only consultancy.
// DRAFT pending UK compliance counsel review (per the original brief).
// When CMS lands in Phase 3, this page will be served from cmsLegal.

export function Privacy() {
  useSeo({
    title: 'Privacy policy — Omega Trade and Finance',
    description: 'How Omega Trade and Finance Ltd (Companies House 17206902) collects, uses and protects personal data under the UK GDPR and DPA 2018.',
    canonicalPath: '/privacy',
  });
  return (
    <main>
      <section className="section legal-hero">
        <div className="container">
          <span className="eyebrow">Privacy policy</span>
          <h1>How we collect, use and protect your data.</h1>
          <p className="lead" style={{ marginTop: 18, maxWidth: '64ch' }}>
            This notice explains what personal data Omega Trade and Finance Ltd processes about you,
            why, and your rights under the UK GDPR and the Data Protection Act 2018.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">

          <Clause title="1. Who we are">
            <p>
              Omega Trade and Finance Ltd ("<strong>Omega</strong>", "<strong>we</strong>") is the
              data controller for personal data processed in connection with this website and our
              services. We are a private company limited by shares, registered in England & Wales
              with company number <strong>17206902</strong>.
            </p>
            <p>For privacy queries: <Mono>enquiry@omegatrade.co.uk</Mono></p>
          </Clause>

          <Clause title="2. Personal data we collect">
            <p>We collect personal data when you:</p>
            <ul className="checklist">
              <li><strong>Submit the contact form</strong> on this website (name, corporate email, phone, company name, the contents of your brief).</li>
              <li><strong>Apply for portal access</strong> (authorised-contact name, role, corporate email, phone, company registration details and eligibility self-certifications).</li>
              <li><strong>Sign in to the portal</strong> (sign-in time, hashed IP address for audit, device user-agent).</li>
              <li><strong>Communicate with us</strong> by email, phone or video call as part of an engagement.</li>
            </ul>
            <p>We also collect minimal technical information automatically — see our <Link to="/cookies">Cookies policy</Link>.</p>
          </Clause>

          <Clause title="3. Why we process your data and our lawful basis">
            <div className="data-table-wrap">
              <table className="data-table">
                <thead><tr><th>Purpose</th><th>Lawful basis</th></tr></thead>
                <tbody>
                  <tr><td>Responding to enquiries submitted via the contact form</td><td>Legitimate interests (Art. 6(1)(f) UK GDPR) — progressing prospective B2B engagements</td></tr>
                  <tr><td>Onboarding clients and operating the portal</td><td>Performance of a contract (Art. 6(1)(b))</td></tr>
                  <tr><td>KYC / AML checks on clients and counterparties</td><td>Legal obligation (Art. 6(1)(c)) — Money Laundering Regulations 2017</td></tr>
                  <tr><td>Audit log retention</td><td>Legitimate interests + legal obligation</td></tr>
                  <tr><td>Security and fraud prevention</td><td>Legitimate interests</td></tr>
                  <tr><td>Marketing communications (opt-in only)</td><td>Consent (Art. 6(1)(a))</td></tr>
                </tbody>
              </table>
            </div>
          </Clause>

          <Clause title="4. Who we share your data with">
            <p>We share personal data only with sub-processors that support the service:</p>
            <ul className="checklist">
              <li><strong>Microsoft Azure</strong> (UK / EU regions) — application hosting and Kubernetes infrastructure.</li>
              <li><strong>MongoDB Atlas</strong> (UK / EU regions) — primary database for application data.</li>
              <li><strong>Brevo (Sendinblue SAS, France)</strong> — transactional email delivery (sign-in codes, enquiry confirmations, ops notifications).</li>
              <li><strong>Revolut Business (UK)</strong> — payment processing for card and bank-transfer invoicing.</li>
              <li><strong>Authorised counterparties</strong> — only when you instruct an introduction and only the information necessary for that introduction.</li>
              <li><strong>Professional advisers</strong> — accountants, auditors, legal counsel under confidentiality.</li>
              <li><strong>Regulators and authorities</strong> — where required by law (e.g. HMRC, ICO, NCA suspicious-activity reports).</li>
            </ul>
            <p>We do not sell personal data and we do not share it with advertisers.</p>
          </Clause>

          <Clause title="5. International transfers">
            <p>
              Most processing happens in the UK and EU. Where a sub-processor transfers data outside
              the UK / EEA, we rely on UK Adequacy Regulations or the UK International Data Transfer
              Addendum to the EU Standard Contractual Clauses, supplemented by appropriate technical
              and organisational measures.
            </p>
          </Clause>

          <Clause title="6. How long we keep your data">
            <ul className="checklist">
              <li><strong>Public enquiries</strong> that do not convert to an engagement: deleted after 12 months.</li>
              <li><strong>Client and engagement records:</strong> retained for 7 years after the end of the relationship, in line with AML/CTF obligations under MLR 2017.</li>
              <li><strong>Audit log</strong> (immutable record of state changes, KYC reviews, sign-ins, document access): 7 years.</li>
              <li><strong>Marketing-list contacts</strong>: until you withdraw consent.</li>
            </ul>
          </Clause>

          <Clause title="7. Your rights">
            <p>Under the UK GDPR you have the right to:</p>
            <ul className="checklist">
              <li>Request a copy of the personal data we hold about you (subject access).</li>
              <li>Ask us to rectify inaccurate or incomplete data.</li>
              <li>Ask us to erase data we no longer have a lawful basis to keep ("right to be forgotten").</li>
              <li>Restrict or object to certain processing.</li>
              <li>Receive your data in a portable format.</li>
              <li>Withdraw consent at any time where processing is based on consent.</li>
              <li>Not be subject to decisions based solely on automated processing that produce legal or similarly significant effects (we do not currently make such decisions).</li>
            </ul>
            <p>
              To exercise any of these rights, email <Mono>enquiry@omegatrade.co.uk</Mono>. We will
              respond within one calendar month. You also have the right to complain to the UK
              Information Commissioner's Office at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a> if
              you believe we have not handled your data correctly.
            </p>
          </Clause>

          <Clause title="8. Security">
            <p>
              Personal data is encrypted in transit (TLS 1.2+) and at rest. Portal sessions are
              authenticated with passwordless magic-link codes and short-lived signed cookies.
              Access to client data inside Omega is role-gated and every access is recorded in the
              immutable audit log.
            </p>
          </Clause>

          <Clause title="9. Cookies">
            <p>
              We use the minimum cookies required to operate the portal sign-in flow. See our{' '}
              <Link to="/cookies">Cookies policy</Link> for the full list.
            </p>
          </Clause>

          <Clause title="10. Changes to this notice">
            <p>
              We may update this notice from time to time. Material changes will be flagged on the
              portal at next sign-in. The current version is dated <Mono>{LAST_UPDATED}</Mono>.
            </p>
          </Clause>

          <div className="legal-closer">
            <strong>Last updated</strong>: {LAST_UPDATED}. For any privacy query, write to{' '}
            <Mono>enquiry@omegatrade.co.uk</Mono> or our registered office.
          </div>
        </div>
      </section>
    </main>
  );
}

function Clause({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="legal-block">
      <h2>{title}</h2>
      <div className="body">{children}</div>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{children}</span>;
}
